import { inject, Injectable } from '@angular/core';
import { audit, auditTime, BehaviorSubject, catchError, EMPTY, filter, of, switchMap, timer } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../security/services/auth.service';
import { KeepAliveApiService } from './api/keep-alive-api.service';

const keepAliveDate = 'keep-alive-date';

const waitTimeBeforeDetectingChanges = 1000;

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class KeepAliveService {
  private readonly router: Router = inject(Router);
  private readonly cookieService: CookieService = inject(CookieService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly api: KeepAliveApiService = inject(KeepAliveApiService);

  private readonly lastInteractionDateSubj$: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(new Date());

  private readonly keepAlive$ = this.lastInteractionDateSubj$.pipe(
    untilDestroyed(this),
    auditTime(waitTimeBeforeDetectingChanges),
    filter((newDate: Date | null) => newDate !== null && this.authService.isAuthenticated()),
    switchMap((newDate: Date | null) => (newDate ? of(newDate) : EMPTY)),
    audit((newDate: Date) => {
      const oldDate = this.getDateFromCookies();
      const timout = (this.getDiffBetweenTwoDates(oldDate, newDate) ?? 0) - environment.keepAliveUpdateTimeout;

      return timout > 0 ? of(newDate) : timer(environment.keepAliveUpdateTimeout);
    }),
    switchMap(() => {
      this.updateCookie(new Date());

      return this.api.updateKeepAlive().pipe(catchError(() => EMPTY));
    })
  );

  constructor() {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationStart)
      )
      .subscribe(() => this.logInteraction());

    this.keepAlive$.subscribe();
  }

  public logInteraction(): void {
    const date = new Date();
    const isAuthenticated = this.authService.isAuthenticated();

    this.lastInteractionDateSubj$.next(isAuthenticated ? date : null);
  }

  private getDateFromCookies(): Date | null {
    const date = this.cookieService.get(keepAliveDate);
    return date ? new Date(date) : null;
  }

  private updateCookie(newDate: Date): void {
    this.cookieService.set(keepAliveDate, newDate.toISOString(), 1);
  }

  private getDiffBetweenTwoDates(oldDate: Date | null, newDate: Date | null): number | null {
    return newDate && oldDate ? newDate.getTime() - oldDate.getTime() : null;
  }
}
